import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

function useVisible(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function ContactHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative h-[420px] md:h-[520px] flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20luxury%20hotel%20lobby%20interior%20with%20warm%20amber%20lighting%2C%20elegant%20marble%20reception%20desk%2C%20tall%20windows%20with%20natural%20light%20streaming%20in%2C%20sophisticated%20minimalist%20design%2C%20Florida%20upscale%20hospitality%20setting%2C%20wide%20angle%20architectural%20photography%2C%20clean%20lines%2C%20warm%20neutral%20tones%2C%20inviting%20professional%20atmosphere%2C%20no%20people%2C%20serene%20and%20refined&width=1920&height=900&seq=contact-hero-360hg-v1&orientation=landscape"
          alt="Contact 360 Hospitality Group"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/55 to-slate-950/20"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/40 to-transparent"></div>
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pb-14 md:pb-20">
        <div className={`max-w-2xl transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-5">
            <div className="h-px w-8 bg-amber-400"></div>
            <span className="text-xs font-medium text-amber-400 uppercase tracking-[0.25em]">Get in Touch</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-light text-white leading-tight tracking-tight mb-4">
            Let&apos;s Talk
          </h1>
          <p className="text-base md:text-lg text-gray-300 font-light leading-relaxed max-w-xl">
            Whether you&apos;re an owner exploring management options, an investor evaluating your next acquisition, or a brand partner looking to expand — we&apos;d love to hear from you.
          </p>
        </div>
      </div>
    </section>
  );
}

function ContactMain() {
  const { ref, visible } = useVisible(0.05);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value ?? '';
    if (message.length > 500) return;
    setSubmitting(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    await fetch('https://readdy.ai/api/form/d7b65uburf8q17puvp90', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    });
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <section ref={ref} className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12 xl:gap-20 items-start">

          {/* ── LEFT: Contact Details ── */}
          <div className={`lg:col-span-2 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>

            {/* Corporate Office */}
            <div className="mb-10">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em] mb-5">Corporate Office</p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-50 rounded-lg shrink-0 mt-0.5">
                    <i className="ri-building-4-line text-amber-600 text-base"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 mb-0.5">360 Hospitality Group</p>
                    <p className="text-sm font-light text-slate-500 leading-relaxed">
                      301 Tucker Lane<br />Cocoa, Florida 32926
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                    <i className="ri-phone-line text-amber-600 text-base"></i>
                  </div>
                  <a href="tel:3213368093" className="text-sm font-light text-slate-600 hover:text-amber-600 transition-colors whitespace-nowrap">
                    321-336-8093
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                    <i className="ri-mail-line text-amber-600 text-base"></i>
                  </div>
                  <a href="mailto:info@360hospitalitygroup.com" className="text-sm font-light text-slate-600 hover:text-amber-600 transition-colors">
                    info@360hospitalitygroup.com
                  </a>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-100 mb-10"></div>

            {/* Business Development */}
            <div className="mb-10">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em] mb-5">Business Development</p>
              <p className="text-xs text-slate-400 font-light mb-5 leading-relaxed">
                For partnership and management inquiries, reach out directly to our BD team:
              </p>
              <div className="space-y-5">
                {[
                  {
                    name: 'April Scramlin',
                    title: 'Office manager,',
                    email: 'april@360hospitalitygroup.com',
                    phone: '(321) 336-8093',
                  },
                ].map((contact, i) => (
                  <div key={i} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="text-sm font-medium text-slate-900 mb-0.5">{contact.name}</p>
                    <p className="text-xs text-amber-600 font-medium uppercase tracking-wide mb-3">{contact.title}</p>
                    <div className="space-y-1.5">
                      <a href={`tel:${contact.phone.replace(/\D/g, '')}`} className="flex items-center gap-2 text-xs text-slate-500 hover:text-amber-600 transition-colors">
                        <i className="ri-phone-line text-amber-500 text-xs"></i>
                        <span className="font-light whitespace-nowrap">{contact.phone}</span>
                      </a>
                      <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-xs text-slate-500 hover:text-amber-600 transition-colors">
                        <i className="ri-mail-line text-amber-500 text-xs"></i>
                        <span className="font-light truncate">{contact.email}</span>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-100 mb-10"></div>

            {/* Response promise */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
              <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg shrink-0 mt-0.5">
                <i className="ri-time-line text-amber-600 text-base"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900 mb-0.5">Quick Response Guaranteed</p>
                <p className="text-xs text-slate-500 font-light leading-relaxed">
                  Our team typically responds within <strong className="text-slate-700 font-medium">1 business day</strong>. For urgent matters, call us directly.
                </p>
              </div>
            </div>

            {/* Map */}
            <div className="mt-8 rounded-xl overflow-hidden border border-slate-100 h-52">
              <iframe
                title="360 Hospitality Group Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3510.0!2d-80.7417!3d28.3861!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e0a0a0a0a0a0a0%3A0x0!2s301+Tucker+Ln%2C+Cocoa%2C+FL+32926!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* ── RIGHT: Form ── */}
          <div className={`lg:col-span-3 transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="bg-white rounded-2xl border border-slate-100 p-8 md:p-10">
              {submitted ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-6">
                    <i className="ri-check-line text-4xl text-amber-600"></i>
                  </div>
                  <h3 className="text-3xl font-light text-slate-900 mb-3">Message Received</h3>
                  <p className="text-base text-slate-500 font-light leading-relaxed max-w-sm mx-auto mb-8">
                    A 360 senior partner will be in touch within 1 business day. We look forward to learning more about your goals.
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center space-x-2 text-sm font-medium text-amber-600 hover:text-amber-500 transition-colors"
                  >
                    <i className="ri-arrow-left-line"></i>
                    <span>Back to Homepage</span>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="mb-8">
                    <h2 className="text-2xl font-light text-slate-900 mb-1">Start the Conversation</h2>
                    <p className="text-sm text-slate-500 font-light">Fill out the form below and a senior partner will reach out within 1 business day.</p>
                  </div>

                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">

                    {/* Full Name */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wide">
                        Full Name <span className="text-amber-500">*</span>
                      </label>
                      <input
                        name="full_name"
                        required
                        type="text"
                        placeholder="John Smith"
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>

                    {/* Email + Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wide">
                          Email Address <span className="text-amber-500">*</span>
                        </label>
                        <input
                          name="email"
                          required
                          type="email"
                          placeholder="john@yourcompany.com"
                          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wide">
                          Phone Number
                        </label>
                        <input
                          name="phone"
                          type="tel"
                          placeholder="(321) 000-0000"
                          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors"
                        />
                      </div>
                    </div>

                    {/* Company / Property */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wide">
                        Company / Property Name
                      </label>
                      <input
                        name="company_property"
                        type="text"
                        placeholder="e.g. Sunset Inn, Orlando FL"
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>

                    {/* Inquiry Type + Num Properties */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wide">
                          Inquiry Type <span className="text-amber-500">*</span>
                        </label>
                        <div className="relative">
                          <select
                            name="inquiry_type"
                            required
                            defaultValue=""
                            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors appearance-none bg-white cursor-pointer"
                          >
                            <option value="" disabled>Select one...</option>
                            <option value="Hotel Owner - Management Partner">I&apos;m a Hotel Owner looking for a management partner</option>
                            <option value="Investor - New Opportunities">I&apos;m an Investor exploring new opportunities</option>
                            <option value="Brand Partner">I&apos;m a Brand Partner</option>
                            <option value="General Inquiry">I have a general inquiry</option>
                          </select>
                          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-down-s-line text-slate-400 text-base"></i>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wide">
                          Number of Properties
                        </label>
                        <div className="relative">
                          <select
                            name="num_properties"
                            defaultValue=""
                            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors appearance-none bg-white cursor-pointer"
                          >
                            <option value="">Select...</option>
                            <option value="1">1</option>
                            <option value="2-5">2–5</option>
                            <option value="6-10">6–10</option>
                            <option value="10+">10+</option>
                          </select>
                          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-down-s-line text-slate-400 text-base"></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide">Message</label>
                        <span className={`text-xs font-light ${charCount > 480 ? 'text-rose-500' : 'text-slate-400'}`}>
                          {charCount}/500
                        </span>
                      </div>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        placeholder="Tell us about your property, investment goals, or how we can help..."
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                      />
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 disabled:opacity-60 text-white font-medium text-sm tracking-wide transition-all whitespace-nowrap cursor-pointer flex items-center justify-center space-x-2"
                    >
                      {submitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-base"></i>
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <span>Start the Conversation</span>
                          <i className="ri-arrow-right-line text-base"></i>
                        </>
                      )}
                    </button>

                    <p className="text-xs text-slate-400 font-light text-center">
                      Our team typically responds within 1 business day. Your information is never shared or sold.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactCTAStrip() {
  const { ref, visible } = useVisible();
  return (
    <section ref={ref} className="py-20 bg-slate-950 relative overflow-hidden">
      {/* Subtle glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] bg-amber-500/8 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 text-center">
        <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-xs font-medium text-amber-500 uppercase tracking-[0.3em] mb-5">Ready to Grow?</p>
          <h2 className="text-4xl md:text-5xl font-light text-white leading-tight mb-5">
            Ready to grow your portfolio?<br />
            <span className="text-amber-400 italic">Let&apos;s build something together.</span>
          </h2>
          <p className="text-base text-gray-400 font-light max-w-xl mx-auto mb-10 leading-relaxed">
            One conversation with a 360 senior partner is all it takes to see what&apos;s possible for your property.
          </p>
          <a
            href="#contact-form"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="inline-flex items-center space-x-2 px-10 py-4 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 text-white text-sm font-medium tracking-wide transition-all whitespace-nowrap cursor-pointer"
          >
            <span>Contact Us Today</span>
            <i className="ri-arrow-right-line text-base"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

export default function ContactPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <ContactHero />
      <ContactMain />
      <ContactCTAStrip />
      <Footer />
    </div>
  );
}
